import { z } from 'zod';

export enum RequestStatus {
  DRAFT = 'DRAFT',
  // WHY (#960): pre-work China state. `send` moves DRAFT/RETURN to
  //   `READY_FOR_WORK` (SLA timer starts here); the first carrier add
  //   atomically moves the row to `IN_WORK`. This issue (#960) is the
  //   shared/DB foundation only — backend transition behavior changes
  //   in #961, web display in #962. Russia requests do NOT adopt this
  //   flow and continue DRAFT -> IN_WORK directly via their own
  //   transition matrix in `russia-requests.ts`.
  READY_FOR_WORK = 'READY_FOR_WORK',
  IN_WORK = 'IN_WORK',
  PROCESSED = 'PROCESSED',
  RETURN = 'RETURN',
  CLOSED = 'CLOSED',
  ARCHIVE = 'ARCHIVE',
  // WHY (#585): terminal business outcomes that replace the single
  // `CLOSED` collapse. `PROCESSED -> SUCCESS` requires a winning bid
  // (paid by the manager) and `PROCESSED -> REFUSED` requires a
  // structured `RequestRefusalReason`. Both stamp `closedAt` so
  // downstream "is finalized?" predicates can use the same column.
  SUCCESS = 'SUCCESS',
  REFUSED = 'REFUSED',
  // WHY (#1835): active, non-terminal "parked" state. Foundation only —
  //   no transition produces or leaves POSTPONED yet (that lands in
  //   #1836/#1837), and it is deliberately excluded from
  //   TERMINAL_REQUEST_STATUSES / DRAFT_LIKE_REQUEST_STATUSES /
  //   LOGIST_HIDDEN_STATUSES so it behaves like an open request.
  POSTPONED = 'POSTPONED',
  // WHY (#2133): terminal "our-fault" negative outcome — the China Logist
  //   declares «Не можем везти», distinct from a customer REFUSED. Terminal
  //   like REFUSED (added to TERMINAL_REQUEST_STATUSES + isTerminalRequestStatus
  //   and to archive.from), but deliberately NOT draft-like and NOT
  //   logist-hidden. Foundation only (#2099 epic): the `cannotTransport`
  //   transition is in the shared matrix but no endpoint/UI surfaces it yet —
  //   the callable path lands in #2136 and the web action in #2139.
  CANNOT_TRANSPORT = 'CANNOT_TRANSPORT',
}

// WHY (#585): rows in any terminal/closed state. `closedAt` is non-null
// for these statuses on non-archived rows (invariant enforced by the
// app layer; no DB CHECK constraint). Archive may carry CLOSED/SUCCESS/
// REFUSED/CANNOT_TRANSPORT in `archivedFromStatus`; unarchive restores it.
export const TERMINAL_REQUEST_STATUSES = [
  RequestStatus.CLOSED,
  RequestStatus.SUCCESS,
  RequestStatus.REFUSED,
  RequestStatus.CANNOT_TRANSPORT,
] as const;
export type TerminalRequestStatus = (typeof TERMINAL_REQUEST_STATUSES)[number];
export const isTerminalRequestStatus = (status: RequestStatus): status is TerminalRequestStatus =>
  status === RequestStatus.CLOSED ||
  status === RequestStatus.SUCCESS ||
  status === RequestStatus.REFUSED ||
  status === RequestStatus.CANNOT_TRANSPORT;

// WHY (#585): structured refusal reason stored on `Request.refusalReason`
// when the customer decides not to pay for a processed quote. Append-only
// machine codes — the wire layer rejects strings outside this set so the
// future analytics path (out of scope here) can rely on stable values.
//
// QUOTE_RESPONSE_TOO_SLOW — the carrier quote arrived too late for the
//   customer's decision window. Concerns response timing, not transit
//   duration.
// PRICE_NOT_ACCEPTED — the offered price exceeded customer budget /
//   expectations.
// CLIENT_UNRESPONSIVE — the customer did not provide a decision in time.
// OTHER — fallback when none of the canonical codes fit; pair with a
//   refusal comment for context.
export enum RequestRefusalReason {
  QUOTE_RESPONSE_TOO_SLOW = 'QUOTE_RESPONSE_TOO_SLOW',
  PRICE_NOT_ACCEPTED = 'PRICE_NOT_ACCEPTED',
  CLIENT_UNRESPONSIVE = 'CLIENT_UNRESPONSIVE',
  OTHER = 'OTHER',
}

export const RequestRefusalReasonSchema = z.nativeEnum(RequestRefusalReason);

// WHY (#585): wire bound for `Request.refusalComment` — free text, may
// contain PII, trimmed, bounded so log lines / audit ledger payloads
// stay finite. Empty-after-trim normalises to null at the service layer.
export const REFUSAL_COMMENT_MAX_LENGTH = 1000;

// WHY (#585): default + bounds for `RequestCarrierBid.validityDays`.
// Re-exported so the web edit modal can render the input bounds without
// duplicating literals.
export const CARRIER_BID_VALIDITY_DAYS_DEFAULT = 14;
export const CARRIER_BID_VALIDITY_DAYS_MIN = 1;
export const CARRIER_BID_VALIDITY_DAYS_MAX = 365;

// WHY: `DRAFT` and `RETURN` share a single editing affordance — the owning
// Manager (or Admin) opens both in the draft-like aggregate editor, both
// round-trip relaxed partial values through `RequestSchema`, and both are
// hard-deletable. Centralizing the membership avoids `status === DRAFT ||
// status === RETURN` duplication across shared, backend, and frontend.
// Non-draft-like statuses (`IN_WORK`, `PROCESSED`, `CLOSED`, `ARCHIVE`)
// remain strict on every wire surface and reject partial values.
export const DRAFT_LIKE_REQUEST_STATUSES = [RequestStatus.DRAFT, RequestStatus.RETURN] as const;
export type DraftLikeRequestStatus = (typeof DRAFT_LIKE_REQUEST_STATUSES)[number];
export const isDraftLikeRequestStatus = (status: RequestStatus): status is DraftLikeRequestStatus =>
  status === RequestStatus.DRAFT || status === RequestStatus.RETURN;

// WHY (#1159): visibility-narrowing twin of `DRAFT_LIKE_REQUEST_STATUSES`.
// The set is identical today (DRAFT, RETURN) but the policy intent is
// distinct: `DRAFT_LIKE_*` governs the draft-like editing affordance
// (relaxed wire validation, hard delete) while `LOGIST_HIDDEN_STATUSES`
// governs read visibility — these statuses represent manager-owned
// work-in-progress, and logist actors (`logist_cn` on `Request`,
// `logist_ru` on `RussiaRequest`) are removed from the surface entirely
// (list, deep-link, sub-resources, realtime, notifications, unread
// aggregates) for rows in either status.
//
// INVARIANT: the two constants are maintained side-by-side in this file
// so future status-taxonomy edits cannot silently drift the editing
// surface and the visibility surface apart. A regression where they
// disagree is not a typing error — it is a policy split between the
// "who may edit a manager-owned row" and "who may see a manager-owned
// row" axes. Keep both updated in lockstep.
//
// @aiSecurity load-bearing — every backend access helper and frontend
// chip/default resolver imports this constant. Do not inline literal
// `[DRAFT, RETURN]` lists at call sites; that scatters the policy.
export const LOGIST_HIDDEN_STATUSES = [RequestStatus.DRAFT, RequestStatus.RETURN] as const;
export type LogistHiddenStatus = (typeof LOGIST_HIDDEN_STATUSES)[number];
export const isStatusHiddenFromLogist = (status: RequestStatus): status is LogistHiddenStatus =>
  status === RequestStatus.DRAFT || status === RequestStatus.RETURN;

// WHY (#1178): the China-side logist can reassign `RequestBoxItem`s into
// visual groups (`groupOrdinal`) inside a single box only while the request
// is in `IN_WORK`. The narrow regroup endpoint guards on this set; all 8
// other RequestStatus members reject with `GATE_FAILED` + `request.status.gate`.
// Kept as a single-member array (not a scalar) so future extensions of the
// allowed surface stay a single source of truth on both backend and web.
//
// INVARIANT: the regroup endpoint authority guard (`assertCanRegroupBoxItems`
// on backend, `canRegroupBoxItems` on web) combines this status gate with
// the assigned-logist-or-admin actor rule and a `request.kind !== 'CONTAINER'`
// shape check; this constant only owns the status axis.
export const LOGIST_REGROUP_ALLOWED_STATUSES = [RequestStatus.IN_WORK] as const;
export type LogistRegroupAllowedStatus = (typeof LOGIST_REGROUP_ALLOWED_STATUSES)[number];
export const isRegroupAllowedStatus = (
  status: RequestStatus,
): status is LogistRegroupAllowedStatus => status === RequestStatus.IN_WORK;

/**
 * @aiPurpose realtime-audience narrowing helper for the hidden-status
 *   axis (#1159 PR4). When a row's CURRENT status is hidden from
 *   logist actors, the assigned `logistId` is removed from the audience
 *   so subsequent sub-resource / chat / patch broadcasts do not reach
 *   the logist socket (which would refetch and 404 against the PR1/PR3
 *   read gate — generating noise and confirming row existence).
 *
 *   `oneShotLogistEvict=true` opts out: callers that want a SINGLE
 *   final broadcast to the logist (e.g., the transition-INTO-hidden
 *   case where the logist client needs one signal to evict the row
 *   from its cache) pass this flag. The China-side transition
 *   orchestrator drives that eviction naturally by emitting the
 *   freshly-locked audience as-is — only standalone callers that
 *   need a manual eviction emit should set this flag.
 * @aiSecurity scattering this narrowing across call sites re-opens
 *   the leak surface this issue closes. Backend services that build
 *   a realtime audience for a `Request` or `RussiaRequest` mutation
 *   MUST route through this helper rather than inlining the
 *   `isStatusHiddenFromLogist(status) ? null : audience.logistId`
 *   ternary.
 */
export function narrowAudienceForHiddenStatus<A extends { logistId: string | null }>(
  audience: A,
  rowStatus: RequestStatus,
  opts: { oneShotLogistEvict?: boolean } = {},
): A {
  if (opts.oneShotLogistEvict) return audience;
  if (!isStatusHiddenFromLogist(rowStatus)) return audience;
  return { ...audience, logistId: null };
}

export enum RequestCarrierState {
  PENDING = 'PENDING',
  ANSWERED = 'ANSWERED',
  REJECTED = 'REJECTED',
}

export const CarrierBidCurrencySchema = z.enum(['USD', 'RUB', 'CNY']);
export type CarrierBidCurrency = z.infer<typeof CarrierBidCurrencySchema>;

export enum AuditAction {
  CREATE = 'CREATE',
  TRANSITION = 'TRANSITION',
  EDIT = 'EDIT',
  CHAT = 'CHAT',
  CARRIER_ADD = 'CARRIER_ADD',
  CARRIER_EDIT = 'CARRIER_EDIT',
  WINNER = 'WINNER',
  FILES = 'FILES',
}

// WHY: backend per-request ledger needs an action vocabulary that names
// each transition and carrier mutation distinctly so the per-request
// drawer's audit feed can render specific copy ("заявка отправлена в
// работу" vs a generic "переход статуса"). The legacy `AuditAction`
// stays for the in-memory web prototype until the consumer migration
// removes it.
export enum RequestAuditAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  TRANSITION_SEND = 'TRANSITION_SEND',
  // WHY (#960): implicit `READY_FOR_WORK -> IN_WORK` transition emitted
  //   atomically when the FIRST carrier is added (no explicit `send`
  //   POST). Distinguished from `TRANSITION_SEND` so the audit ledger
  //   can render specific copy ("заявка автоматически в работе" vs
  //   "заявка отправлена в работу") and so test/UI consumers can filter
  //   on the trigger semantics. This issue (#960) is the shared/DB
  //   foundation only — the auto-transition is written in #961.
  TRANSITION_AUTO_START = 'TRANSITION_AUTO_START',
  TRANSITION_RETURN = 'TRANSITION_RETURN',
  TRANSITION_PROCESS = 'TRANSITION_PROCESS',
  // WHY (#1059): explicit `PROCESSED -> IN_WORK` reopen by the assigned
  //   logist_cn when more logistics work is needed. Distinct from
  //   `TRANSITION_RETURN` (which targets RETURN, not IN_WORK) so the
  //   per-request feed renders "заявка переоткрыта" instead of
  //   "возвращена". Reopen audit payload also carries the reopen-cycle
  //   metadata (reopenedAt, reopenedCount, winnerBidIdBefore).
  TRANSITION_REOPEN = 'TRANSITION_REOPEN',
  TRANSITION_CLOSE = 'TRANSITION_CLOSE',
  TRANSITION_ARCHIVE = 'TRANSITION_ARCHIVE',
  TRANSITION_UNARCHIVE = 'TRANSITION_UNARCHIVE',
  TRANSITION_SUCCESS = 'TRANSITION_SUCCESS',
  TRANSITION_REFUSE = 'TRANSITION_REFUSE',
  // WHY (#1836): explicit `PROCESSED -> POSTPONED` parking by the manager.
  //   Distinct from the finalisation codes so the per-request feed renders
  //   "request parked" rather than a success/refuse outcome; POSTPONED is
  //   active/non-terminal and clears no fields.
  TRANSITION_POSTPONE = 'TRANSITION_POSTPONE',
  // WHY (#2133): the China «cannot transport» foundation. TRANSITION_CANNOT_TRANSPORT
  //   stamps the entry IN_WORK -> CANNOT_TRANSPORT; TRANSITION_CANNOT_TRANSPORT_REVIVE
  //   stamps the automatic CANNOT_TRANSPORT -> IN_WORK revive on the first real
  //   bid. Foundation/parity only here — no service emits either code yet
  //   (#2136/#2137). Mirrored into the Prisma `RequestAuditAction` enum in the
  //   same PR; scripts/check-enum-sync.sh enforces the shared <-> Prisma parity.
  TRANSITION_CANNOT_TRANSPORT = 'TRANSITION_CANNOT_TRANSPORT',
  TRANSITION_CANNOT_TRANSPORT_REVIVE = 'TRANSITION_CANNOT_TRANSPORT_REVIVE',
  WINNER = 'WINNER',
  CARRIER_ADD = 'CARRIER_ADD',
  CARRIER_UPDATE = 'CARRIER_UPDATE',
  CARRIER_REMOVE = 'CARRIER_REMOVE',
  CHAT_POSTED = 'CHAT_POSTED',
  CHAT_MARKED_UNREAD = 'CHAT_MARKED_UNREAD',
  CHAT_UNMARKED = 'CHAT_UNMARKED',
  // INVARIANT (#707): emitted on the China parent `Request` when a
  // `RussiaRequest` snapshot is minted via `createFromParent`. Paired
  // with one `RussiaRequestAuditEntry.CREATE` row on the new RU record
  // inside the same Prisma `$transaction`. The legacy `AuditAction`
  // mapping in `to-wire.ts` maps this to `EDIT` since the legacy enum
  // has no "linked-child" concept.
  CHILD_RUSSIA_CREATED = 'CHILD_RUSSIA_CREATED',
  // WHY (#1178): one batch ledger row per successful regroup PATCH on the
  // narrow `/requests/:id/boxes/:boxId/items/group-ordinals` endpoint.
  // Payload carries `{ boxId, changes: [{ itemId, from, to }] }` per D10.
  // Failed PATCH (validation, 412 stale, 409 override conflict) writes no
  // row; no-op batch (every requested ordinal already matches DB) writes
  // no row either (D16).
  BOX_REGROUP = 'BOX_REGROUP',
}

export const SLA_HOURS = 72;
export const SLA_WARN_HOURS = 24;
export const SLA_ERR_HOURS = 8;
